/*
 * Magic Afro — price range slider for the collection filter sidebar.
 *
 * Live magicafro.fr shows a two-handle slider under the Prix min/max inputs.
 * Horizon's price facet is input-only, so this adds the slider and wires it
 * to the existing min/max text inputs:
 *   - dragging a handle live-updates its text input (preview)
 *   - releasing a handle fires `change` on that input, so Horizon's
 *     price-facet-component (`on:change="/updatePriceFilterAndResults"`)
 *     re-filters the results — no custom networking needed.
 *   - typing in an input re-syncs the handles.
 *
 * Registered once (from layout/theme.liquid). connectedCallback re-runs when
 * the facets are re-rendered by the Section Rendering API after each filter
 * change, so the slider rebuilds itself automatically.
 */
(function () {
  if (customElements.get('magic-price-slider')) return;

  const num = (v) => {
    if (v == null) return NaN;
    // money_without_currency may be "31,90" or "1 234,56" → 31.9 / 1234.56
    const s = String(v).replace(/[^\d.,-]/g, '').replace(/\s/g, '').replace(',', '.');
    const n = parseFloat(s);
    return isNaN(n) ? NaN : n;
  };

  customElements.define(
    'magic-price-slider',
    class extends HTMLElement {
      connectedCallback() {
        // Guard against double-init on the same element.
        if (this._built) return;
        this._built = true;

        const facet = this.closest('price-facet-component') || this.parentElement;
        if (!facet) return;
        const inputs = facet.querySelectorAll('.price-facet__input');
        const minInput = inputs[0];
        const maxInput = inputs[1];
        if (!minInput || !maxInput) return;

        const RMIN = 0;
        const RMAX = Math.max(1, Math.ceil(num(this.dataset.max) || num(maxInput.dataset.max) || 0));
        if (!RMAX) return;

        let curMin = Math.round(num(this.dataset.curMin));
        let curMax = Math.round(num(this.dataset.curMax));
        if (isNaN(curMin)) curMin = RMIN;
        if (isNaN(curMax)) curMax = RMAX;
        curMin = Math.min(Math.max(curMin, RMIN), RMAX);
        curMax = Math.min(Math.max(curMax, RMIN), RMAX);

        this.innerHTML =
          '<div class="magic-pslider__track"></div>' +
          '<input type="range" class="magic-pslider__range magic-pslider__range--min" aria-label="Prix minimum">' +
          '<input type="range" class="magic-pslider__range magic-pslider__range--max" aria-label="Prix maximum">';

        const track = this.querySelector('.magic-pslider__track');
        const rMin = this.querySelector('.magic-pslider__range--min');
        const rMax = this.querySelector('.magic-pslider__range--max');
        [rMin, rMax].forEach((r) => { r.min = RMIN; r.max = RMAX; r.step = 1; });
        rMin.value = curMin;
        rMax.value = curMax;

        // Paint the active range as a track gradient (sits under the
        // transparent range inputs, so it's always visible behind the thumbs).
        const TRACK = '#e2ddd3';
        const FILL = '#323328';
        const paint = () => {
          const lo = (Number(rMin.value) - RMIN) / (RMAX - RMIN) * 100;
          const hi = (Number(rMax.value) - RMIN) / (RMAX - RMIN) * 100;
          track.style.background =
            'linear-gradient(to right,' +
            TRACK + ' 0%,' + TRACK + ' ' + lo + '%,' +
            FILL + ' ' + lo + '%,' + FILL + ' ' + hi + '%,' +
            TRACK + ' ' + hi + '%,' + TRACK + ' 100%)';
        };

        const setText = (input, val) => {
          input.value = String(val);
        };

        // Dragging (live preview, no network)
        rMin.addEventListener('input', () => {
          if (Number(rMin.value) > Number(rMax.value)) rMin.value = rMax.value;
          setText(minInput, rMin.value == RMIN ? '' : rMin.value);
          if (rMin.value == RMIN) minInput.value = '';
          paint();
        });
        rMax.addEventListener('input', () => {
          if (Number(rMax.value) < Number(rMin.value)) rMax.value = rMin.value;
          setText(maxInput, rMax.value == RMAX ? '' : rMax.value);
          if (rMax.value == RMAX) maxInput.value = '';
          paint();
        });

        // Release → trigger Horizon's re-filter via the text input's change.
        const fire = (input) => input.dispatchEvent(new Event('change', { bubbles: true }));
        rMin.addEventListener('change', () => fire(minInput));
        rMax.addEventListener('change', () => fire(maxInput));

        // Typing in the inputs re-syncs the handles.
        const syncFromInputs = () => {
          const lo = minInput.value === '' ? RMIN : Math.round(num(minInput.value));
          const hi = maxInput.value === '' ? RMAX : Math.round(num(maxInput.value));
          if (!isNaN(lo)) rMin.value = Math.min(Math.max(lo, RMIN), RMAX);
          if (!isNaN(hi)) rMax.value = Math.min(Math.max(hi, RMIN), RMAX);
          paint();
        };
        minInput.addEventListener('change', syncFromInputs);
        maxInput.addEventListener('change', syncFromInputs);

        paint();
      }
    }
  );
})();
